// Child lock — the Settings control, shared by both shells.
//
// ⚠️ TWO BUTTONS, NOT A SWITCH, AND THAT IS THE HONEST SHAPE.
//
// A Switch component claims to show the CURRENT state. Nothing here can know it:
// the device's status frame carries `s_switch[0..n-1]` and nothing else, so the
// lock state never reaches the app. A toggle would have to invent its own
// position, be wrong the moment anyone locked the panel from elsewhere, and —
// worst of the three — read as "child lock is on" when it might not be. On a
// safety feature that is the failure that matters.
//
// So: two actions that claim nothing. Press Lock, the command goes out. What the
// device did with it is not something this screen is able to report, and it says
// so rather than guessing.
//
// If the firmware ever includes the lock state in the status frame, this becomes
// a real toggle and the note below comes out.

import React, { useState } from 'react';
import {
  Box, Typography, Button, Divider, Stack,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { useApp } from '../../core/state/AppContext';
import { publishCommand } from '../../core/utils/deviceController';
import { childLockCommand } from '../../core/devices/childLock';

export default function ChildLockPanel({ device, dense = false }) {
  const { userId } = useApp();
  const [sent, setSent] = useState(null);   // 'lock' | 'unlock' | null

  const send = (lock) => {
    publishCommand(device.UniqueNumber, childLockCommand(lock), userId);
    setSent(lock ? 'lock' : 'unlock');
  };

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
        Child lock
      </Typography>
      <Divider sx={{ mb: 1 }} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
        Locks the switch&apos;s own buttons. The app keeps working.
      </Typography>

      <Stack direction="row" spacing={1}>
        <Button
          size={dense ? 'small' : 'medium'}
          variant="outlined"
          startIcon={<LockIcon />}
          onClick={() => send(true)}
          sx={{ textTransform: 'none' }}
        >
          Lock buttons
        </Button>
        <Button
          size={dense ? 'small' : 'medium'}
          variant="outlined"
          startIcon={<LockOpenIcon />}
          onClick={() => send(false)}
          sx={{ textTransform: 'none' }}
        >
          Unlock
        </Button>
      </Stack>

      {/* Says the command WENT, never that it worked — the device does not
          report its lock state, so "sent" is the whole of what we know. */}
      {sent && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {sent === 'lock' ? 'Lock' : 'Unlock'}
          {' sent. This device does not report its lock state, so check the '}
          switch itself.
        </Typography>
      )}
    </Box>
  );
}
